import React, { useState, useEffect, useCallback } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { BookingRecord, Creator, Voucher, TicketOption } from "../types";

// API Services
import {
  getOrders,
  updateOrderStatus as apiUpdateOrderStatus,
  getTodayVisitors,
} from "../services/api/admin/orders";
import { getAdminTickets } from "../services/api/admin/tickets";
import { getVouchers } from "../services/api/admin/affiliates";
import {
  getNotifications,
  markAsRead as apiMarkAsRead,
  markAllAsRead as apiMarkAllAsRead,
  Notification,
} from "../services/api/admin/notifications";
import {
  subscribeToUserNotifications,
  subscribeToRoleNotifications,
  NotificationEventData,
} from "../services/api/realtime";
import { getStoredUser } from "../services/api/auth";
import Swal from "sweetalert2";

import { AdminSidebar } from "./admin/AdminSidebar";
import { TopBar, AdminNotification } from "./admin/TopBar";
import { DashboardHome } from "./admin/DashboardHome";
import { OrdersManager } from "./admin/OrdersManager";
import { FinanceReport } from "./admin/FinanceReport";
import { CustomerList } from "./admin/CustomerList";
import { AffiliateProgram } from "./admin/AffiliateProgram";
import { TicketManagement } from "./admin/TicketManagement";
import { OrderDetailModal } from "./admin/OrderDetailModal";
import { CheckInGate } from "./admin/CheckInGate";
import { BlogManager } from "./admin/BlogManager";
import { WhatsAppGateway } from "./admin/WhatsAppGateway";
import { PaymentSettings } from "./admin/PaymentSettings";
import { LicenseManager } from "./admin/LicenseManager";
import { WithdrawalManager } from "./admin/WithdrawalManager";
import { LinkhubManager } from "./admin/LinkhubManager";

interface AdminDashboardProps {
  onLogout: () => void;
}

// Role-based route protection component
const RoleRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles: string[];
}> = ({ children, allowedRoles }) => {
  const user = getStoredUser();
  const userRole = user?.role || "";

  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

type ViewState =
  | "dashboard"
  | "orders"
  | "finance"
  | "customers"
  | "affiliate"
  | "tickets"
  | "checkin"
  | "blog"
  | "whatsapp"
  | "payment"
  | "license"
  | "withdrawals"
  | "linkhub";

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentView = location.pathname.split("/").pop() || "dashboard";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingRecord | null>(
    null
  );
  const [modalSource, setModalSource] = useState<"orders" | "finance">(
    "orders"
  );
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Convert API notification to AdminNotification format
  const mapNotification = (
    n: Notification | NotificationEventData
  ): AdminNotification => ({
    id: n.id,
    type: (n.type as AdminNotification["type"]) || "system",
    title: n.title,
    message: n.message,
    timestamp: new Date(n.createdAt),
    read: n.isRead,
    link: n.actionUrl || undefined,
    data: n.metadata || undefined,
    icon: n.icon || undefined,
    color: n.color || undefined,
    priority: n.priority,
  });

  // Fetch notifications from API on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await getNotifications({ limit: 20 });
        setNotifications(response.data.map(mapNotification));
        setUnreadCount(response.unreadCount);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    fetchNotifications();
  }, []);

  // Subscribe to realtime notifications
  useEffect(() => {
    const user = getStoredUser();
    if (!user) return;

    const unsubscribers: (() => void)[] = [];

    // Subscribe to user-specific notifications
    unsubscribers.push(
      subscribeToUserNotifications(user.id, (data: NotificationEventData) => {
        setNotifications((prev) => {
          if (prev.some((n) => n.id === data.id)) return prev;
          return [mapNotification(data), ...prev].slice(0, 50);
        });
        setUnreadCount((prev) => prev + 1);
      })
    );

    // Subscribe to role-based notifications
    unsubscribers.push(
      subscribeToRoleNotifications(user.role, (data: NotificationEventData) => {
        setNotifications((prev) => {
          if (prev.some((n) => n.id === data.id)) return prev;
          return [mapNotification(data), ...prev].slice(0, 50);
        });
        setUnreadCount((prev) => prev + 1);
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await apiMarkAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await apiMarkAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const refreshAllData = useCallback(async () => {
    // Now components fetch their own data, this is mostly for triggering re-renders if needed
    // or for global features like the check-in success refresh.
  }, []);

  useEffect(() => {
    refreshAllData();
  }, [refreshAllData]);

  const handleViewChange = (view: string) => {
    navigate(view === "dashboard" ? "/admin" : `/admin/${view}`);
    setIsMobileMenuOpen(false);
  };

  const getPageTitle = () => {
    switch (currentView) {
      case "dashboard":
        return "Dashboard Overview";
      case "orders":
        return "Riwayat Pesanan";
      case "finance":
        return "Laporan Keuangan";
      case "customers":
        return "Data Pelanggan";
      case "affiliate":
        return "Program Affiliate";
      case "checkin":
        return "Gate Check-In";
      case "tickets":
        return "Manajemen Tiket";
      case "blog":
        return "Blog & SEO";
      case "whatsapp":
        return "WA Gateway";
      case "payment":
        return "Pembayaran";
      case "license":
        return "Lisensi System";
      case "withdrawals":
        return "Riwayat Withdraw Mitra";
      case "linkhub":
        return "LinkHub Settings";
      default:
        return "Admin Panel";
    }
  };

  const handleStatusChange = async (
    id: string,
    newStatus: BookingRecord["status"]
  ) => {
    const result = await Swal.fire({
      title: "Ganti Status?",
      text: `Ubah status pesanan menjadi ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0F172A",
      confirmButtonText: "Ya, Ganti",
      cancelButtonText: "Batal",
    });

    if (result.isConfirmed) {
      try {
        const statusMap: Record<
          string,
          "pending" | "paid" | "completed" | "cancelled"
        > = {
          PENDING: "pending",
          PAID: "paid",
          COMPLETED: "completed",
          CANCELLED: "cancelled",
        };
        await apiUpdateOrderStatus(id, { status: statusMap[newStatus] });
        await refreshAllData();
        if (selectedBooking && selectedBooking.id === id) {
          setSelectedBooking({ ...selectedBooking, status: newStatus });
        }
        Swal.fire({
          icon: "success",
          title: "Status Diperbarui",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Failed to update status:", error);
        Swal.fire({
          icon: "error",
          title: "Gagal Mengubah Status",
          text: "Silakan coba lagi.",
        });
      }
    }
  };

  const handleDeleteBooking = async (id: string) => {
    const result = await Swal.fire({
      title: "Batalkan Pesanan?",
      text: "Pesanan yang dibatalkan tidak dapat dikembalikan statusnya melalui panel ini.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Ya, Batalkan!",
    });

    if (result.isConfirmed) {
      try {
        await apiUpdateOrderStatus(id, { status: "cancelled" });
        await refreshAllData();
        setSelectedBooking(null);
        Swal.fire({
          icon: "success",
          title: "Pesanan Dibatalkan",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Failed to cancel booking:", error);
        Swal.fire({
          icon: "error",
          title: "Gagal Membatalkan",
          text: "Silakan coba lagi.",
        });
      }
    }
  };

  return (
    <div className="flex h-screen bg-[#F8FAFC] font-sans overflow-hidden">
      <div
        className={`fixed inset-y-0 left-0 z-30 transform transition-transform duration-300 md:relative md:translate-x-0 ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <AdminSidebar
          onLogout={onLogout}
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      <div className="flex-1 flex flex-col h-full min-w-0">
        <TopBar
          onToggleMobileMenu={() => setIsMobileMenuOpen(true)}
          title={getPageTitle()}
          notifications={notifications}
          onMarkAllRead={handleMarkAllRead}
          onMarkAsRead={handleMarkAsRead}
        />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto min-h-full flex flex-col">
            <Routes>
              <Route
                path="/"
                element={<DashboardHome onNavigate={handleViewChange} />}
              />
              <Route
                path="checkin"
                element={
                  <RoleRoute allowedRoles={["admin", "staff"]}>
                    <CheckInGate onCheckInSuccess={refreshAllData} />
                  </RoleRoute>
                }
              />
              <Route
                path="orders"
                element={
                  <OrdersManager
                    onSelectBooking={(b) => {
                      setModalSource("orders");
                      setSelectedBooking(b);
                    }}
                  />
                }
              />
              <Route
                path="finance"
                element={
                  <RoleRoute allowedRoles={["admin", "finance"]}>
                    <FinanceReport
                      onSelectBooking={(b) => {
                        setModalSource("finance");
                        setSelectedBooking(b);
                      }}
                    />
                  </RoleRoute>
                }
              />
              <Route
                path="tickets"
                element={
                  <RoleRoute allowedRoles={["admin", "staff"]}>
                    <TicketManagement onRefresh={refreshAllData} />
                  </RoleRoute>
                }
              />
              <Route
                path="customers"
                element={
                  <RoleRoute allowedRoles={["admin"]}>
                    <CustomerList />
                  </RoleRoute>
                }
              />
              <Route
                path="affiliate"
                element={
                  <RoleRoute allowedRoles={["admin", "staff"]}>
                    <AffiliateProgram onRefresh={refreshAllData} />
                  </RoleRoute>
                }
              />
              <Route
                path="blog"
                element={
                  <RoleRoute allowedRoles={["admin", "staff"]}>
                    <BlogManager />
                  </RoleRoute>
                }
              />
              <Route
                path="whatsapp"
                element={
                  <RoleRoute allowedRoles={["admin"]}>
                    <WhatsAppGateway />
                  </RoleRoute>
                }
              />
              <Route
                path="payment"
                element={
                  <RoleRoute allowedRoles={["admin"]}>
                    <PaymentSettings />
                  </RoleRoute>
                }
              />
              <Route
                path="license"
                element={
                  <RoleRoute allowedRoles={["admin", "finance"]}>
                    <LicenseManager />
                  </RoleRoute>
                }
              />
              <Route
                path="withdrawals"
                element={
                  <RoleRoute allowedRoles={["admin", "finance"]}>
                    <WithdrawalManager />
                  </RoleRoute>
                }
              />
              <Route
                path="linkhub"
                element={
                  <RoleRoute allowedRoles={["admin"]}>
                    <LinkhubManager />
                  </RoleRoute>
                }
              />
            </Routes>
          </div>
        </main>
      </div>

      <OrderDetailModal
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onUpdateStatus={handleStatusChange}
        onDelete={handleDeleteBooking}
        showProfitAnalysis={modalSource === "finance"}
      />
    </div>
  );
};
